import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Visibility as ViewIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
  AccessTime as AccessTimeIcon,
  Person as PersonIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { get } from "../../config/axios";
import { toastMessage } from "../../utils/toastMessage";
import Layout from "../../layout/Main/Layout";

const UserTaskAssignment = () => {
  // State management
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalTasks, setTotalTasks] = useState(0);
  const [taskStats, setTaskStats] = useState({});

  // Dialog states
  const [viewDialog, setViewDialog] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);

  // Filter states
  const [filters, setFilters] = useState({
    status: "",
    sessionNo: "",
    sessionType: "",
    search: "",
  });

  // Constants
  const sessionTypes = ["DISCOVERY", "ANALYSIS", "SOLUTION"];
  const statusTypes = ["ASSIGNED", "IN_PROGRESS", "COMPLETED", "OVERDUE"];

  // Helper function to get phase display name
  const getPhaseDisplayName = (sessionType) => {
    const phaseMap = {
      "DISCOVERY": "Discovery Phase",
      "ANALYSIS": "Manage Phase", 
      "SOLUTION": "Thrive Phase"
    };
    return phaseMap[sessionType] || sessionType;
  };

  // Effects
  useEffect(() => {
    fetchTasks();
    fetchTaskStats();
  }, [page, rowsPerPage, filters]);

  // API Functions
  const fetchTasks = async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: page + 1,
        limit: rowsPerPage,
        ...filters,
      });

      // Add user instance filter to only show user-specific tasks
      queryParams.append('isTemplate', 'false');

      const response = await get(`/api/dashboard/cbt/task-assignments?${queryParams}`);
      if (response?.success) {
        setTasks(Array.isArray(response.data?.assignments) ? response.data.assignments : []);
        setTotalTasks(response.data?.pagination?.totalAssignments || 0);
      } else {
        setTasks([]);
        setTotalTasks(0);
      }
    } catch (error) {
      toastMessage("Failed to fetch user task assignments", "error");
      setTasks([]);
      setTotalTasks(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchTaskStats = async () => {
    try {
      const response = await get("/api/dashboard/cbt/task-assignments/stats");
      if (response?.success) {
        setTaskStats(response.data || {});
      }
    } catch (error) {
      console.error("Failed to fetch task statistics:", error);
    }
  };

  // Helper Functions
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(0);
  };

  const handleView = (task) => {
    setSelectedTask(task);
    setViewDialog(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "ASSIGNED": return "info";
      case "IN_PROGRESS": return "warning";
      case "COMPLETED": return "success";
      case "OVERDUE": return "error";
      default: return "default";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "ASSIGNED": return <AssignmentIcon />;
      case "IN_PROGRESS": return <AccessTimeIcon />;
      case "COMPLETED": return <CheckCircleIcon />;
      case "OVERDUE": return <WarningIcon />;
      default: return <ScheduleIcon />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Not set";
    return new Date(dateString).toLocaleDateString();
  };

  const getUserInitials = (fullName) => {
    if (!fullName) return "U";
    return fullName.split(" ").map(name => name.charAt(0)).join("").toUpperCase();
  };

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h4" gutterBottom>
            User Task Assignments
          </Typography>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => {
              fetchTasks();
              fetchTaskStats();
            }}
          >
            Refresh
          </Button>
        </Box>

        {/* Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
        </Grid>

        {/* Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Filters
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filters.status}
                    onChange={(e) => handleFilterChange("status", e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="">All Statuses</MenuItem>
                    {statusTypes.map((status) => (
                      <MenuItem key={status} value={status}>
                        {status.replace("_", " ")}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Session Number"
                  value={filters.sessionNo}
                  onChange={(e) => handleFilterChange("sessionNo", e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Session Phase</InputLabel>
                  <Select
                    value={filters.sessionType}
                    onChange={(e) => handleFilterChange("sessionType", e.target.value)}
                    label="Session Phase"
                  >
                    <MenuItem value="">All Phases</MenuItem>
                    {sessionTypes.map((type) => (
                      <MenuItem key={type} value={type}>
                        {getPhaseDisplayName(type)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Search"
                  value={filters.search}
                  onChange={(e) => handleFilterChange("search", e.target.value)}
                  placeholder="Search by task title or user name"
                  size="small"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Tasks Table */}
        <Paper sx={{ width: "100%", overflow: "hidden" }}>
          <TableContainer sx={{ maxHeight: 440 }}>
            <Table stickyHeader aria-label="sticky table">
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Task</TableCell>
                  <TableCell>Session</TableCell>
                  <TableCell>Phase</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Assigned Date</TableCell>
                  <TableCell>Due Date</TableCell>
                  <TableCell>Completed Date</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography>Loading...</Typography>
                    </TableCell>
                  </TableRow>
                ) : tasks.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography>No user task assignments found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  tasks.map((task) => (
                    <TableRow hover key={task._id}>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Avatar sx={{ mr: 2, bgcolor: "primary.main" }}>
                            {getUserInitials(task.assignedTo?.fullname)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {task.assignedTo?.fullname || "Unknown User"}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {task.assignedTo?.email || "No email"}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {task.title || "Untitled Task"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {task.description ? task.description.substring(0, 50) + "..." : "No description"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          Session {task.sessionNo}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getPhaseDisplayName(task.sessionType)}
                          size="small"
                          variant="outlined"
                          color={
                            task.sessionType === "DISCOVERY" ? "info" :
                            task.sessionType === "ANALYSIS" ? "warning" :
                            task.sessionType === "SOLUTION" ? "success" : "default"
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={getStatusIcon(task.status)}
                          label={task.status?.replace("_", " ") || "ASSIGNED"}
                          color={getStatusColor(task.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(task.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(task.dueDate)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(task.completedAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => handleView(task)}
                            color="info"
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 100]}
            component="div"
            count={totalTasks}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
          />
        </Paper>

        {/* View Task Dialog */}
        <Dialog
          open={viewDialog}
          onClose={() => setViewDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <AssignmentIcon />
              Task Details
            </Box>
          </DialogTitle>
          <DialogContent>
            {selectedTask && (
              <Box sx={{ mt: 2 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" gutterBottom>
                      Task Information
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Title
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {selectedTask.title}
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                      Description
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {selectedTask.description}
                    </Typography>

                    <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                      Session
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      Session {selectedTask.sessionNo} - {getPhaseDisplayName(selectedTask.sessionType)}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" gutterBottom>
                      User & Status Information
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Assigned To
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <Avatar sx={{ mr: 2, bgcolor: "primary.main" }}>
                        {getUserInitials(selectedTask.assignedTo?.fullname)}
                      </Avatar>
                      <Box>
                        <Typography variant="body1">
                          {selectedTask.assignedTo?.fullname || "Unknown User"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {selectedTask.assignedTo?.email || "No email"}
                        </Typography>
                      </Box>
                    </Box>

                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Status
                    </Typography>
                    <Chip
                      icon={getStatusIcon(selectedTask.status)}
                      label={selectedTask.status?.replace("_", " ") || "ASSIGNED"}
                      color={getStatusColor(selectedTask.status)}
                      sx={{ mb: 2 }}
                    />

                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Dates
                    </Typography>
                    <Typography variant="body2">
                      Assigned: {formatDate(selectedTask.createdAt)}
                    </Typography>
                    <Typography variant="body2">
                      Due: {formatDate(selectedTask.dueDate)}
                    </Typography>
                    {selectedTask.completedAt && (
                      <Typography variant="body2">
                        Completed: {formatDate(selectedTask.completedAt)}
                      </Typography>
                    )}
                  </Grid>

                  {selectedTask.userResponse && (
                    <Grid item xs={12}>
                      <Typography variant="h6" gutterBottom>
                        User Response
                      </Typography>
                      <Paper sx={{ p: 2, bgcolor: "grey.50" }}>
                        <Typography variant="body1">
                          {selectedTask.userResponse}
                        </Typography>
                      </Paper>
                    </Grid>
                  )}
                </Grid>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setViewDialog(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
};

export default UserTaskAssignment; 